import React, {
  useEffect,
  useImperativeHandle,
  useState,
  ForwardRefRenderFunction,
  forwardRef,
  useRef
} from "react";
import "./Radius.css";

import { geodesicBuffer } from "@arcgis/core/geometry/geometryEngine";
import Graphic from "@arcgis/core/Graphic";
import { addressToLocations } from "@arcgis/core/rest/locator";
import Select from "../../../../shared/select/Select";
import SimpleFillSymbol from "@arcgis/core/symbols/SimpleFillSymbol";

export type RadiusHandle = {
  searchCallback: () => Promise<__esri.Point | undefined>;
  clear: () => void;
}

interface iProps {
  mapView: React.MutableRefObject<__esri.MapView>;
  sketchLayer: React.MutableRefObject<__esri.GraphicsLayer>;
  centerPoint: __esri.Point | undefined;
  setRadiusGeometry: React.Dispatch<React.SetStateAction<__esri.Polygon | null>>;
}

const distanceOptions = [25, 50, 100, 150, 300, 500];

const highlightSymbol: __esri.SimpleFillSymbol =
  new SimpleFillSymbol({
    style: "none",
    outline: {
      color: '#327B8F',
      width: 2,
      style: 'solid'
    }
  });
  
const Radius: ForwardRefRenderFunction<RadiusHandle, iProps> = (props, forwardedRef) => {

  const {
    mapView,
    sketchLayer,
    centerPoint,
    setRadiusGeometry,
  } = props;

  useImperativeHandle(forwardedRef, () => ({
    searchCallback,
    clear
  }));

  const [radiusDistance, setRadiusDistance] = useState<number | ''>(100);
  const [radiusUnits, setRadiusUnits] = useState('Miles');
  const [searchText, setSearchText] = useState('');
  const abort = useRef(new AbortController());
 
  const calculateRadiusBuffer = () => {

    if (centerPoint && radiusDistance !== '' && radiusDistance > 0) {
      abort.current?.abort();
      abort.current = new AbortController();

      const buffer = geodesicBuffer(centerPoint, radiusDistance, radiusUnits?.toLowerCase() as __esri.LinearUnits, true) as __esri.Polygon;
      setRadiusGeometry(buffer);
      mapView.current?.goTo(buffer, { signal: abort.current.signal }).catch(console.error);

      let graphics = sketchLayer.current.graphics.filter(x => x.geometry.type === 'polygon').toArray();

      sketchLayer.current.removeMany(graphics);

      sketchLayer.current.add(new Graphic({
        geometry: buffer,
        symbol: highlightSymbol
      }));
    }
  }
  useEffect(calculateRadiusBuffer, [centerPoint, radiusDistance, radiusUnits, setRadiusGeometry, sketchLayer, mapView]);

  const clear = () => {
    sketchLayer.current.removeAll();
    setRadiusGeometry(null);
    setSearchText('');
  }

  const searchCallback = async () => {
    if (!searchText) return undefined;

    const geocodeServiceUrl = 'http://geocode-api.arcgis.com/arcgis/rest/services/World/GeocodeServer';

    try {
      const canidates = await addressToLocations(geocodeServiceUrl, {
        address: { SingleLine: searchText },
        maxLocations: 1,
        outFields: [],
        outSpatialReference: mapView.current?.spatialReference
      });

      if (canidates.length > 0) {
        const location = canidates[0].location;
        return location;
      } else {
        return undefined;
      }
    } catch (e) {
      throw e;
    }
  }

  return (
    <div className="radius-container">
      <div className="grid-area--distance">
        <Select
          label={'Distance Within'}
          labelPosition='top'
          multiSelect={false}
          selectedItems={[radiusDistance.toString()]}
        >
          {distanceOptions.map((option) =>
            <div
              onClick={() => {
                if (!isNaN(option)) {
                  setRadiusDistance(option);
                } else {
                  setRadiusDistance('');
                }
              }}
              className="select-option"
              key={option}
            >
              {option}
            </div>
          )}
        </Select>
      </div>
      <div className="grid-area--distance-unit">
        <Select
          label={' '}
          labelPosition='top'
          multiSelect={false}
          selectedItems={[radiusUnits]}
        >
          {['Miles', 'Kilometers'].map((option) =>
            <div
              onClick={() => setRadiusUnits(option)}
              className="select-option capitalize"
              key={option}
            >
              {option}
            </div>
          )}
        </Select>
      </div>

      <label className="grid-area-to">to</label>
      <div className="grid-area--distance-input">
        <input
          placeholder="City or Zip Code"
          type="text"
          name="to"
          value={searchText}
          onChange={(evt) => setSearchText(evt.target.value)}
        />
      </div>
    </div>
  );
}

export default forwardRef(Radius);